import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import Modal, {
  Field,
  TextInput,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
} from "@/components/FormKit";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import {
  Copy,
  ArrowRight,
  KeyRound,
  Eye,
  LogIn as LogInIcon,
  AtSign,
  Building2,
} from "lucide-react";

const initial = {
  name: "",
  email: "",
  phone: "",
  address: "",
  website: "",
};

export default function Companies() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [lastCreated, setLastCreated] = useState(null); // { company, psa_user }
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newPsaPwd, setNewPsaPwd] = useState(null); // { email, password }
  const [psaEmailEdit, setPsaEmailEdit] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/platform/companies");
    setItems(r.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadDetail = useCallback(async (id) => {
    const r = await api.get(`/platform/companies/${id}`);
    setDetail(r.data);
    setPsaEmailEdit(r.data?.psa_user?.email || "");
  }, []);

  useEffect(() => {
    if (detailId) loadDetail(detailId);
    else {
      setDetail(null);
      setNewPsaPwd(null);
    }
  }, [detailId, loadDetail]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/platform/companies", form);
      setOpen(false);
      setForm(initial);
      setLastCreated(data);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (c, status) => {
    try {
      await api.patch(`/platform/companies/${c.id}`, { status });
      toast.success(`Company ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const impersonate = async (c) => {
    try {
      await api.post(`/platform/companies/${c.id}/impersonate`);
      toast.success(`Entering ${c.name}…`);
      await refresh();
      navigate("/company", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetPsaPassword = async () => {
    if (!detail) return;
    try {
      const { data } = await api.post(
        `/platform/companies/${detail.id}/psa/reset-password`
      );
      setNewPsaPwd(data);
      toast.success("PSA password reset");
      loadDetail(detail.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePsaEmail = async () => {
    if (!detail || !psaEmailEdit || psaEmailEdit === detail.psa_user?.email) return;
    try {
      await api.patch(`/platform/companies/${detail.id}/psa`, {
        email: psaEmailEdit,
      });
      toast.success("PSA email updated");
      loadDetail(detail.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "customer_code",
        label: "Code",
        render: (r) => (
          <span className="font-mono text-xs font-bold text-brand-700 bg-brand-50 px-2 py-1">
            {r.customer_code || "—"}
          </span>
        ),
      },
      {
        key: "name",
        label: "Company",
        render: (r) => (
          <div>
            <div className="font-bold">{r.name}</div>
            <div className="text-xs text-stone-500">{r.email}</div>
          </div>
        ),
      },
      {
        key: "plan",
        label: "Plan",
        render: (r) =>
          r.plan ? (
            <div>
              <div className="font-semibold">{r.plan.name}</div>
              <div className="text-xs text-stone-500 font-mono">
                {fmtCurrency(r.plan.price)}/mo
              </div>
            </div>
          ) : (
            <span className="text-stone-400 text-xs">No plan</span>
          ),
      },
      {
        key: "usage",
        label: "Usage",
        render: (r) =>
          r.plan ? (
            <div className="text-xs font-mono space-y-0.5">
              <div>Proj {r.usage?.projects}/{r.plan.max_projects}</div>
              <div>Cust {r.usage?.customers}/{r.plan.max_customers}</div>
              <div>Users {r.usage?.portal_users}/{r.plan.max_portal_users}</div>
            </div>
          ) : (
            "—"
          ),
      },
      { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
      { key: "created_at", label: "Joined", render: (r) => fmtDate(r.created_at) },
      {
        key: "actions",
        label: "Actions",
        render: (r) => (
          <div className="flex flex-wrap gap-1">
            <GhostButton
              onClick={() => setDetailId(r.id)}
              testId={`view-${r.id}`}
            >
              <Eye size={12} className="inline mr-1" /> View
            </GhostButton>
            <GhostButton
              onClick={() => impersonate(r)}
              testId={`enter-${r.id}`}
            >
              <LogInIcon size={12} className="inline mr-1" /> Enter
            </GhostButton>
            {r.status !== "suspended" && (
              <GhostButton
                onClick={() => toggleStatus(r, "suspended")}
                testId={`suspend-${r.id}`}
              >
                Suspend
              </GhostButton>
            )}
            {r.status !== "active" && (
              <GhostButton
                onClick={() => toggleStatus(r, "active")}
                testId={`activate-${r.id}`}
              >
                Activate
              </GhostButton>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="space-y-8" data-testid="platform-companies-page">
      <PageHeader
        eyebrow="Tenants"
        title="Construction companies"
        description="Onboard developers, assign plans, and enter any tenant workspace for support."
        actions={
          <PrimaryButton onClick={() => setOpen(true)} testId="new-company-button">
            + New Tenant
          </PrimaryButton>
        }
      />

      <DataTable
        testId="companies-table"
        columns={columns}
        rows={items}
        empty="No companies onboarded yet. Create the first one."
      />

      {/* ---------------- New tenant modal (minimal) ---------------- */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New tenant"
        testId="new-company-modal"
      >
        <p className="text-sm text-stone-600 mb-4">
          A <span className="font-bold text-stone-950">PSA admin account</span>{" "}
          (<span className="font-mono">psa-&lt;code&gt;@mysavvy.in / Admin@123</span>)
          will be auto-provisioned for this tenant. You can rename or reset it later.
        </p>
        <form onSubmit={submit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Company name">
              <TextInput
                testId="form-company-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Company email">
              <TextInput
                testId="form-company-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field label="Phone">
              <TextInput
                testId="form-company-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Field>
            <Field label="Website">
              <TextInput
                testId="form-company-website"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Address">
                <TextInput
                  testId="form-company-address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </Field>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <SecondaryButton
              type="button"
              onClick={() => setOpen(false)}
              testId="cancel-new-company"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={busy}
              testId="submit-new-company"
            >
              {busy ? "Creating…" : "Create tenant"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>

      {/* ---------------- Success modal with PSA credentials ---------------- */}
      <Modal
        open={!!lastCreated}
        onClose={() => setLastCreated(null)}
        title="Tenant created"
        testId="tenant-created-modal"
      >
        {lastCreated && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 border-l-4 border-green-600 bg-green-50 p-3">
              <Building2 size={18} className="text-green-700" />
              <div>
                <div className="font-bold text-stone-950">
                  {lastCreated.name} ·{" "}
                  <span className="font-mono text-brand-700">
                    #{lastCreated.customer_code}
                  </span>
                </div>
                <div className="text-xs text-stone-600">
                  PSA admin account auto-provisioned.
                </div>
              </div>
            </div>
            <div className="border border-stone-300 bg-stone-50 p-4">
              <div className="overline text-stone-500 mb-2">PSA credentials</div>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm text-stone-950">
                  {lastCreated.psa_user?.email}
                </span>
                <GhostButton
                  onClick={() => copy(lastCreated.psa_user?.email)}
                  testId="copy-psa-email"
                >
                  <Copy size={12} className="inline mr-1" /> Copy
                </GhostButton>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1.5">
                <span className="font-mono text-sm text-stone-950">
                  pw: {lastCreated.psa_user?.default_password}
                </span>
                <GhostButton
                  onClick={() => copy(lastCreated.psa_user?.default_password)}
                  testId="copy-psa-pwd"
                >
                  <Copy size={12} className="inline mr-1" /> Copy
                </GhostButton>
              </div>
            </div>
            <div className="text-xs text-stone-500">
              Share these credentials with the tenant, or hit "Enter workspace" on
              the tenant row to log in as this PSA user.
            </div>
            <div className="flex justify-end">
              <PrimaryButton
                onClick={() => setLastCreated(null)}
                testId="tenant-created-done"
              >
                Done <ArrowRight size={14} className="inline ml-1" />
              </PrimaryButton>
            </div>
          </div>
        )}
      </Modal>

      {/* ---------------- Details drawer ---------------- */}
      <Modal
        open={!!detailId}
        onClose={() => setDetailId(null)}
        title={detail ? `${detail.name} · #${detail.customer_code}` : "Tenant details"}
        testId="tenant-detail-modal"
        maxWidth="max-w-2xl"
      >
        {detail ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="overline text-stone-500 mb-1">Email</div>
                <div className="font-mono text-stone-950">{detail.email || "—"}</div>
              </div>
              <div>
                <div className="overline text-stone-500 mb-1">Phone</div>
                <div className="font-mono text-stone-950">{detail.phone || "—"}</div>
              </div>
              <div>
                <div className="overline text-stone-500 mb-1">Website</div>
                <div className="font-mono text-stone-950 break-all">
                  {detail.website || "—"}
                </div>
              </div>
              <div>
                <div className="overline text-stone-500 mb-1">Status</div>
                <StatusBadge value={detail.status} />
              </div>
              <div className="col-span-2">
                <div className="overline text-stone-500 mb-1">Address</div>
                <div className="text-stone-950">{detail.address || "—"}</div>
              </div>
            </div>

            {/* PSA card */}
            <div className="border border-stone-300 bg-stone-50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="overline text-brand-600">PSA admin user</div>
                {detail.psa_user?.is_psa && (
                  <span className="text-[10px] tracking-wider font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5">
                    AUTO-PROVISIONED
                  </span>
                )}
              </div>
              {detail.psa_user ? (
                <>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Field label="Email">
                        <TextInput
                          testId="psa-email-input"
                          type="email"
                          value={psaEmailEdit}
                          onChange={(e) => setPsaEmailEdit(e.target.value)}
                        />
                      </Field>
                    </div>
                    <div className="pb-4">
                      <SecondaryButton
                        type="button"
                        onClick={savePsaEmail}
                        testId="save-psa-email"
                      >
                        <AtSign size={12} className="inline mr-1" /> Save
                      </SecondaryButton>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <SecondaryButton
                      type="button"
                      onClick={resetPsaPassword}
                      testId="reset-psa-password"
                    >
                      <KeyRound size={12} className="inline mr-1" /> Reset password
                    </SecondaryButton>
                    <PrimaryButton
                      type="button"
                      onClick={() => impersonate(detail)}
                      testId="enter-workspace-detail"
                    >
                      <LogInIcon size={12} className="inline mr-1" /> Enter workspace
                    </PrimaryButton>
                  </div>
                  {newPsaPwd && (
                    <div className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm">
                      <div className="font-bold text-stone-950">
                        New password (shown once):
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="font-mono text-stone-950">
                          {newPsaPwd.new_password}
                        </span>
                        <GhostButton
                          onClick={() => copy(newPsaPwd.new_password)}
                          testId="copy-new-pwd"
                        >
                          <Copy size={12} className="inline mr-1" /> Copy
                        </GhostButton>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-stone-500">
                  No PSA user on this tenant yet.
                </div>
              )}
            </div>

            {/* Subscription */}
            <div>
              <div className="overline text-stone-500 mb-2">Subscription</div>
              {detail.subscription && detail.plan ? (
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-stone-500">Plan</div>
                    <div className="font-bold">{detail.plan.name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-stone-500">Price</div>
                    <div className="font-mono">
                      {fmtCurrency(detail.plan.price)}/{detail.plan.billing_cycle}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-stone-500">Status</div>
                    <StatusBadge value={detail.subscription.status} />
                  </div>
                </div>
              ) : (
                <div className="text-sm text-stone-400">No active subscription.</div>
              )}
            </div>

            {/* Users */}
            <div>
              <div className="overline text-stone-500 mb-2">
                Company users ({detail.users?.length || 0})
              </div>
              <div className="space-y-1.5">
                {(detail.users || []).map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between text-sm border-b border-stone-100 pb-1.5"
                  >
                    <div>
                      <div className="font-mono text-stone-950">{u.email}</div>
                      <div className="text-xs text-stone-500">
                        {u.name} · {u.role}
                        {u.is_psa && (
                          <span className="ml-1 text-amber-700">· PSA</span>
                        )}
                      </div>
                    </div>
                    <StatusBadge value={u.status || "active"} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-stone-500 py-6">Loading…</div>
        )}
      </Modal>
    </div>
  );
}
